// 拾遗响应窗口：
// 触发时机——任何玩家替换装备产生弃牌、闪电/杀戮之心/缴械等被动弃牌、
// 以及防具判定摸牌弃牌（discardReason=passive|judgment）后立即开窗，
// 手中持有「拾遗」效果牌的玩家可依次选择立即取回一张；3 秒不操作默认放弃。
import type { GameState, PickupWindow, PickupResume } from "@/types/game";
import type { DeckCard } from "@/types/cards";
import { canPickFromDiscard } from "./Deck";
import { isFrozen, isEffectLocked } from "./Status";
import { pushEvent } from "./Events";

// 某玩家当前能否使用拾遗（窗口与主动出牌共用同一资格）
export function canUsePickup(state: GameState, playerId: string): boolean {
  const p = state.players.find((x) => x.id === playerId);
  if (!p || !p.isAlive || p.isDying) return false;
  if (isFrozen(p) || isEffectLocked(p)) return false; // 寒冰/无间炼狱不能打出手牌/效果牌
  return p.hand.some((c) => c.name === "拾遗" && c.kind === "效果牌");
}

// 若存在可拾遗的新牌与可行动的拾遗持有者，则开窗；否则原样返回
export function openPickupWindow(
  state: GameState,
  newCards: DeckCard[],
  victimId: string,
  reason: string,
  resume?: PickupResume
): GameState {
  if (state.phase === "game-over" || state.pickupWindow) return state;
  // 只认本批新弃中仍在弃牌堆、且可拾遗的牌
  const pileUids = new Set(state.discardPile.map((c) => c.uid));
  const cards = newCards.filter(
    (c) => pileUids.has(c.uid) && canPickFromDiscard(c)
  );
  if (cards.length === 0) return state;
  const responders = state.players
    .filter((p) => canUsePickup(state, p.id))
    .map((p) => p.id);
  if (responders.length === 0) return state;
  const decisions: PickupWindow["decisions"] = {};
  for (const rid of responders) decisions[rid] = "pending";
  let next: GameState = {
    ...state,
    phase: "pickup-response",
    pickupWindow: {
      cards,
      victimId,
      reason,
      responders,
      decisions,
      ...(resume ? { resume } : {}),
    },
  };
  next = pushEvent(next, {
    type: "pickup-window",
    victimId,
    reason,
    count: cards.length,
  });
  return next;
}

// 当前待决策的拾遗玩家
export function currentPickupResponder(
  w: PickupWindow
): string | undefined {
  return w.responders.find((rid) => w.decisions[rid] === "pending");
}

// 关闭窗口，回到执行阶段
function closeWindow(state: GameState): GameState {
  return { ...state, phase: "play", pickupWindow: null };
}

// 处理拾遗决策：cardUid=null 表示放弃
export function resolvePickupDecision(
  state: GameState,
  playerId: string,
  cardUid: number | null,
  targetUid: number | null
): GameState {
  const w = state.pickupWindow;
  if (!w) throw new Error("无活动拾遗窗口");
  if (w.decisions[playerId] !== "pending") {
    throw new Error(`该玩家已决策: ${playerId}`);
  }

  // 放弃：记录后推进到下一位
  if (cardUid === null) {
    const decisions = { ...w.decisions, [playerId]: "pass" as const };
    const advanced: GameState = {
      ...state,
      pickupWindow: { ...w, decisions },
    };
    return currentPickupResponder({ ...w, decisions } as PickupWindow)
      ? advanced
      : closeWindow(advanced);
  }

  // 拾遗：校验手牌与目标
  const player = state.players.find((p) => p.id === playerId)!;
  const pickupCard = player.hand.find((c) => c.uid === cardUid);
  if (!pickupCard || pickupCard.name !== "拾遗") {
    throw new Error("手中没有拾遗牌");
  }
  const target = state.discardPile.find((c) => c.uid === targetUid);
  if (!target || !canPickFromDiscard(target)) {
    throw new Error("目标牌不可拾遗");
  }
  if (!w.cards.some((c) => c.uid === target.uid)) {
    throw new Error("只能拾遗本批新弃的牌");
  }
  // 拾遗牌进入弃牌堆（自己打出，不可再被拾遗），目标牌进手牌
  let next: GameState = {
    ...state,
    discardPile: [
      ...state.discardPile.filter((c) => c.uid !== target.uid),
      { ...pickupCard, discardReason: "play" as const },
    ],
  };
  next = {
    ...next,
    players: next.players.map((p) =>
      p.id === playerId
        ? {
            ...p,
            hand: [...p.hand.filter((c) => c.uid !== cardUid), target],
          }
        : p
    ),
  };
  next = pushEvent(next, {
    type: "transfer",
    fromId: "弃牌堆",
    toId: playerId,
    cardId: target.id,
    reason: "拾遗",
  });
  // 一人拾遗成功，窗口即关闭（其余本批牌留弃牌堆，仍可在执行阶段主动拾遗）
  return closeWindow(next);
}
